import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import {
  fetchAdminOrganizationBySlug,
  fetchCurrentUserInfo,
  fetchShops,
} from "../api.js";
import { Button } from "../components/ui/button.jsx";

export default function OrgDashboard() {
  const { orgSlug } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [shops, setShops] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const me = await fetchCurrentUserInfo();
        if (!mounted) return;
        setUserInfo(me);

        const orgData = await fetchAdminOrganizationBySlug(orgSlug);
        if (!mounted) return;
        setOrg(orgData);

        if (me.role === "admin" && !me.org_id) {
          setError("Admin sem organizacao atribuida.");
          setLoading(false);
          return;
        }

        if (me.role === "admin" && me.org_id && me.org_id !== orgData.id) {
          setError("Sem permissao para aceder a esta organizacao.");
          setLoading(false);
          return;
        }

        const list = await fetchShops();
        if (!mounted) return;
        const filtered = orgData?.id
          ? list.filter((shop) => shop.org_id === orgData.id)
          : list;
        setShops(filtered);
      } catch (err) {
        setError(err.response?.data?.detail || "Erro ao carregar organizacao");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [orgSlug]);

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <p className="text-white text-center">A carregar...</p>
      </main>
    );
  }

  if (!org || error) {
    return (
      <main className="min-h-screen bg-background p-6 md:p-12">
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg max-w-2xl mx-auto">
          {error || "Organizacao nao encontrada"}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gold mb-2">
                {org.name}
              </h1>
              <p className="text-muted text-lg">
                Bem-vindo, {user?.user_metadata?.name || user?.email || "equipa"}.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate(`/admin/${orgSlug}/shops/new`)}>
                Criar salao
              </Button>
              {userInfo?.role === "superadmin" && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => navigate(`/admin/${orgSlug}/admins/new`)}
                >
                  Criar admin
                </Button>
              )}
              <Button variant="outline" size="default" onClick={handleLogout}>
                Terminar sessao
              </Button>
            </div>
          </div>
        </header>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {shops.length === 0 ? (
          <p className="text-muted text-center">Nenhum salao registado.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shops.map((shop) => (
              <div
                key={shop.id}
                onClick={() => navigate(`/admin/${orgSlug}/shops/${shop.id}`)}
                className={`bg-card border border-border rounded-lg p-6 cursor-pointer hover:border-gold/50 transition-all ${
                  shop.active === false ? "opacity-50" : ""
                }`}
              >
                <h3 className="text-xl font-semibold text-white mb-2">
                  {shop.name}
                </h3>
                <div className="space-y-1 mb-3">
                  {shop.address && (
                    <p className="text-sm text-muted">{shop.address}</p>
                  )}
                  {shop.contact_email && (
                    <p className="text-sm text-muted">{shop.contact_email}</p>
                  )}
                  {shop.contact_phone && (
                    <p className="text-sm text-muted">{shop.contact_phone}</p>
                  )}
                </div>
                <span
                  className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                    shop.active === false
                      ? "bg-red-500/10 text-red-400"
                      : "bg-green-500/10 text-green-400"
                  }`}
                >
                  {shop.active === false ? "Inativo" : "Ativo"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
